import { db } from '../config/firebase';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  collectionGroup,
  addDoc,
  limit,
  startAfter,
  getCountFromServer
} from 'firebase/firestore';
import { sendOrderNotificationToSeller, sendOrderNotificationToCustomer } from './emailService';

// Collezioni
const SHOPS_COLLECTION = 'shops';
const PRODUCTS_SUBCOLLECTION = 'products';

// Funzioni per i negozi
export const createShop = async (shopData) => {
  try {
    const shopRef = doc(db, SHOPS_COLLECTION, shopData.ownerId);
    await setDoc(shopRef, {
      ...shopData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      status: 'active'
    });
    return shopRef.id;
  } catch (error) {
    console.error('Errore nella creazione del negozio:', error);
    throw error;
  }
};

// Funzioni di utilità per la conversione dei nomi
export const convertShopNameToUrl = (name) => {
  return name.replace(/\s+/g, '_');
};

export const convertUrlToShopName = (url) => {
  return url.replace(/_/g, ' ');
};

export const getShop = async (shopId) => {
  const shopRef = doc(db, SHOPS_COLLECTION, shopId);
  const shopDoc = await getDoc(shopRef);
  return shopDoc.exists() ? { id: shopDoc.id, ...shopDoc.data() } : null;
};

export const getAllShops = async () => {
  const shopsRef = collection(db, SHOPS_COLLECTION);
  const shopsSnap = await getDocs(shopsRef);
  return shopsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const updateShop = async (shopId, shopData) => {
  try {
    const shopRef = doc(db, SHOPS_COLLECTION, shopId);
    await updateDoc(shopRef, {
      ...shopData,
      updatedAt: new Date().toISOString()
    });
    return true;
  } catch (error) {
    console.error('Errore nell\'aggiornamento del negozio:', error);
    throw error;
  }
};

export const deleteShop = async (shopId) => {
  try {
    const shopRef = doc(db, SHOPS_COLLECTION, shopId);
    await deleteDoc(shopRef);
    return true;
  } catch (error) {
    console.error('Errore nell\'eliminazione del negozio:', error);
    throw error;
  }
};

// Funzioni per i prodotti
export const createProduct = async (shopId, productData) => {
  const productRef = doc(collection(db, SHOPS_COLLECTION, shopId, PRODUCTS_SUBCOLLECTION));
  await setDoc(productRef, {
    ...productData,
    shopId,
    createdAt: new Date(),
    updatedAt: new Date()
  });
  return productRef.id;
};

export const getProduct = async (shopId, productId) => {
  const productRef = doc(db, SHOPS_COLLECTION, shopId, PRODUCTS_SUBCOLLECTION, productId);
  const productSnap = await getDoc(productRef);
  return productSnap.exists() ? { id: productSnap.id, ...productSnap.data() } : null;
};

export const getShopProducts = async (shopId, includeDisabled = true) => {
  const productsRef = collection(db, SHOPS_COLLECTION, shopId, PRODUCTS_SUBCOLLECTION);
  const querySnapshot = await getDocs(productsRef);
  const products = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  if (!includeDisabled) {
    return products.filter(p => !p.disabled);
  }
  return products;
};

// Recupera tutte le categorie uniche dai prodotti del negozio
export const getAllProductCategories = async (shopId, includeDisabled = true) => {
  const products = await getShopProducts(shopId, includeDisabled);
  return [...new Set(products.map(p => p.category).filter(Boolean))].sort();
};

export const updateProduct = async (shopId, productId, productData) => {
  const productRef = doc(db, SHOPS_COLLECTION, shopId, PRODUCTS_SUBCOLLECTION, productId);
  return await updateDoc(productRef, {
    ...productData,
    updatedAt: new Date().toISOString()
  });
};

export const deleteProduct = async (shopId, productId) => {
  const productRef = doc(db, SHOPS_COLLECTION, shopId, PRODUCTS_SUBCOLLECTION, productId);
  return await deleteDoc(productRef);
};

export const toggleProductDisabled = async (shopId, productId, disabled) => {
  const productRef = doc(db, SHOPS_COLLECTION, shopId, PRODUCTS_SUBCOLLECTION, productId);
  return await updateDoc(productRef, {
    disabled,
    updatedAt: new Date().toISOString()
  });
};

// Funzioni di ricerca
// NOTA: queste funzioni usano range query Firestore (>=, <=) che supportano
// solo PREFIX match, non substring. Per una vera ricerca substring, usa
// client-side filtering con toLowerCase().includes().
// Vedi: useDebounce hook + client-side filter in Home, ShopDetail, Seller/Products.

/**
 * @deprecated Usa client-side filtering con toLowerCase().includes()
 */
export const searchShops = async (searchTerm) => {
  try {
    const shopsRef = collection(db, SHOPS_COLLECTION);
    const shopsQuery = query(
      shopsRef,
      where('name', '>=', searchTerm),
      where('name', '<=', searchTerm + '\uf8ff')
    );
    const shopsSnap = await getDocs(shopsQuery);
    
    return shopsSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Errore nella ricerca dei negozi:', error);
    throw error;
  }
};

/**
 * @deprecated Usa client-side filtering con toLowerCase().includes()
 */
export const searchProducts = async (shopId, searchTerm) => {
  try {
    const productsRef = collection(db, SHOPS_COLLECTION, shopId, PRODUCTS_SUBCOLLECTION);
    const productsQuery = query(
      productsRef,
      where('name', '>=', searchTerm),
      where('name', '<=', searchTerm + '\uf8ff')
    );
    const productsSnap = await getDocs(productsQuery);
    
    return productsSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Errore nella ricerca dei prodotti:', error);
    throw error;
  }
};

// Cerca prodotti in TUTTI i negozi (per homepage)
/**
 * @deprecated Usa client-side filtering con toLowerCase().includes() sui dati già caricati.
 *             Home page ora filtra direttamente i featuredShops in memoria.
 */
export const searchAllShopsProducts = async (searchTerm) => {
  const shops = await getAllShops();
  const results = [];

  // Range case-insensitive come in buildProductsQuery
  const lowerBound = searchTerm.charAt(0).toUpperCase() + searchTerm.slice(1).toLowerCase();
  const upperBound = searchTerm.toLowerCase() + '';

  for (const shop of shops) {
    try {
      const productsRef = collection(db, SHOPS_COLLECTION, shop.id, PRODUCTS_SUBCOLLECTION);
      const q = query(
        productsRef,
        where('name', '>=', lowerBound),
        where('name', '<=', upperBound)
      );
      const snapshot = await getDocs(q);
      snapshot.docs.forEach(doc => {
        results.push({
          id: doc.id,
          ...doc.data(),
          shopId: shop.id,
          shopName: shop.name,
          shopLogo: shop.logoUrl,
          shopSubtitle: shop.subtitle,
          shopRating: shop.rating
        });
      });
    } catch (err) {
      console.error(`Errore nella ricerca prodotti per il negozio ${shop.name}:`, err);
    }
  }

  return results;
};

// Funzioni per la gestione del negozio
export const getShopByOwner = async (ownerId) => {
  const shopsRef = collection(db, SHOPS_COLLECTION);
  const q = query(shopsRef, where('ownerId', '==', ownerId));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

// Funzioni per la gestione degli ordini
export const getShopOrders = async (shopId) => {
  const ordersRef = collection(db, SHOPS_COLLECTION, shopId, 'orders');
  const querySnapshot = await getDocs(ordersRef);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const updateOrderStatus = async (shopId, orderId, status) => {
  const orderRef = doc(db, SHOPS_COLLECTION, shopId, 'orders', orderId);
  return await updateDoc(orderRef, {
    status,
    updatedAt: new Date().toISOString()
  });
};

// Funzioni per le statistiche
export const getShopStats = async (shopId) => {
  const shopRef = doc(db, SHOPS_COLLECTION, shopId);
  const shopDoc = await getDoc(shopRef);
  
  if (!shopDoc.exists()) return null;

  const productsRef = collection(db, SHOPS_COLLECTION, shopId, 'products');
  const ordersRef = collection(db, SHOPS_COLLECTION, shopId, 'orders');

  const [productsSnapshot, ordersSnapshot] = await Promise.all([
    getDocs(productsRef),
    getDocs(ordersRef)
  ]);

  const totalProducts = productsSnapshot.size;
  const totalOrders = ordersSnapshot.size;
  const totalRevenue = ordersSnapshot.docs.reduce((sum, doc) => {
    const order = doc.data();
    return sum + (order.total || 0);
  }, 0);

  return {
    totalProducts,
    totalOrders,
    totalRevenue,
    lastUpdated: new Date().toISOString()
  };
};

export const addProduct = async (shopId, productData) => {
  const productsRef = collection(db, SHOPS_COLLECTION, shopId, PRODUCTS_SUBCOLLECTION);
  return await addDoc(productsRef, {
    ...productData,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  });
};

export const getShopByName = async (shopName) => {
  const shopsRef = collection(db, SHOPS_COLLECTION);
  const q = query(shopsRef, where('name', '==', shopName));
  const querySnapshot = await getDocs(q);
  if (querySnapshot.empty) return null;
  const doc = querySnapshot.docs[0];
  return { id: doc.id, ...doc.data() };
};

// Funzioni per la gestione delle categorie
export const getShopCategories = async (shopId) => {
  const shopRef = doc(db, SHOPS_COLLECTION, shopId);
  const shopDoc = await getDoc(shopRef);
  return shopDoc.exists() ? shopDoc.data().categories || [] : [];
};

export const updateShopCategories = async (shopId, categories) => {
  const shopRef = doc(db, SHOPS_COLLECTION, shopId);
  return await updateDoc(shopRef, {
    categories,
    updatedAt: new Date().toISOString()
  });
};

// Sincronizza la categoria di un prodotto nel documento shop.
// Se la categoria non è già presente nell'array categories del negozio, la aggiunge.
export const syncProductCategory = async (shopId, category) => {
  if (!category) return;
  const shopRef = doc(db, SHOPS_COLLECTION, shopId);
  const shopDoc = await getDoc(shopRef);
  if (!shopDoc.exists()) return;
  const existingCategories = shopDoc.data().categories || [];
  if (!existingCategories.includes(category)) {
    await updateDoc(shopRef, {
      categories: [...existingCategories, category],
      updatedAt: new Date().toISOString()
    });
  }
};

export const createOrder = async (shopId, orderData) => {
  try {
    // Recupera i dati del negozio per le notifiche
    const shopData = await getShop(shopId);

    // Ottieni l'ultimo ordine per generare il nuovo ID
    const ordersRef = collection(db, SHOPS_COLLECTION, shopId, 'orders');
    const ordersQuery = query(ordersRef, orderBy('orderNumber', 'desc'), limit(1));
    const lastOrderSnapshot = await getDocs(ordersQuery);
    
    // Genera il nuovo numero d'ordine
    let newOrderNumber = 1;
    if (!lastOrderSnapshot.empty) {
      const lastOrder = lastOrderSnapshot.docs[0].data();
      newOrderNumber = (lastOrder.orderNumber || 0) + 1;
    }

    // Crea un nuovo documento con ID generato automaticamente
    const newOrderRef = await addDoc(ordersRef, {
      orderNumber: newOrderNumber,
      ...orderData,
      status: 'pending_confirmation',
      paymentStatus: 'pending',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });

    // Ottieni il documento appena creato
    const orderDoc = await getDoc(newOrderRef);
    const order = { id: newOrderRef.id, ...orderDoc.data() };

    // Invia notifiche email (non bloccanti — gli errori email non impediscono la creazione dell'ordine)
    // Recupera l'email del venditore dal profilo utente
    try {
      const sellerProfileRef = doc(db, 'users', shopData.ownerId);
      const sellerProfileDoc = await getDoc(sellerProfileRef);
      const sellerProfile = sellerProfileDoc.exists() ? sellerProfileDoc.data() : null;
      const sellerEmail = sellerProfile?.email || '';

      if (sellerEmail) {
        sendOrderNotificationToSeller({
          sellerEmail,
          sellerName: shopData?.name || 'Venditore',
          orderNumber: newOrderNumber,
          customerName: orderData.customerName,
          total: orderData.total,
          items: orderData.items
        }).catch(err => console.error('Errore notifica venditore:', err));
      } else {
        console.warn('Email venditore non trovata nel profilo per shopId:', shopData.ownerId);
      }
    } catch (err) {
      console.error('Errore nel recupero email venditore per notifica:', err);
    }

    // Recupera l'email del cliente dal profilo utente
    try {
      const customerProfileRef = doc(db, 'users', orderData.customerId);
      const customerProfileDoc = await getDoc(customerProfileRef);
      const customerProfile = customerProfileDoc.exists() ? customerProfileDoc.data() : null;
      const customerEmail = customerProfile?.email || orderData.customerEmail || '';

      if (customerEmail) {
        sendOrderNotificationToCustomer({
          customerEmail,
          customerName: orderData.customerName,
          shopName: shopData?.name || 'Negozio',
          orderNumber: newOrderNumber,
          total: orderData.total,
          items: orderData.items
        }).catch(err => console.error('Errore notifica cliente:', err));
      } else {
        console.warn('Email cliente non trovata nel profilo per customerId:', orderData.customerId);
      }
    } catch (err) {
      console.error('Errore nel recupero email cliente per notifica:', err);
    }

    // Salva il riepilogo ordine nel profilo utente (per "I miei ordini")
    try {
      const userProfileRef = doc(db, 'users', orderData.customerId, 'profile', 'details');
      const userProfileDoc = await getDoc(userProfileRef);
      const existingOrders = userProfileDoc.exists() ? (userProfileDoc.data().orders || []) : [];
      // Verifica che sia un array (potrebbe essere un numero da vecchi dati)
      const ordersArray = Array.isArray(existingOrders) ? existingOrders : [];
      ordersArray.unshift({
        orderId: newOrderRef.id,
        shopId,
        shopName: shopData?.name || 'Negozio',
        orderNumber: newOrderNumber,
        total: orderData.total,
        status: 'pending_confirmation',
        createdAt: new Date().toISOString()
      });
      await setDoc(userProfileRef, { orders: ordersArray }, { merge: true });
    } catch (err) {
      console.error('Errore nel salvataggio ordine nel profilo utente:', err);
    }

    return order;
  } catch (error) {
    console.error('Errore nella creazione dell\'ordine:', error);
    throw error;
  }
};

export const getOrderStatus = async (shopId, orderId) => {
  try {
    const orderRef = doc(db, SHOPS_COLLECTION, shopId, 'orders', orderId);
    const orderDoc = await getDoc(orderRef);
    if (!orderDoc.exists()) return null;
    return orderDoc.data();
  } catch (error) {
    console.error('Errore nel recupero dello stato dell\'ordine:', error);
    throw error;
  }
};

export const updateOrderPaymentStatus = async (shopId, orderId, paymentStatus, paymentMethod = null) => {
  try {
    const orderRef = doc(db, SHOPS_COLLECTION, shopId, 'orders', orderId);
    const updateData = {
      paymentStatus,
      updatedAt: new Date().toISOString()
    };
    if (paymentMethod) {
      updateData.paymentMethod = paymentMethod;
    }
    await updateDoc(orderRef, updateData);
    return true;
  } catch (error) {
    console.error('Errore nell\'aggiornamento dello stato del pagamento:', error);
    throw error;
  }
};

export const updateOrderItem = async (shopId, orderId, itemId, itemData) => {
  try {
    const orderRef = doc(db, 'shops', shopId, 'orders', orderId);
    const orderDoc = await getDoc(orderRef);
    
    if (!orderDoc.exists()) {
      throw new Error('Ordine non trovato');
    }

    const order = orderDoc.data();
    const updatedItems = order.items.map(item => 
      item.id === itemId ? { ...item, ...itemData } : item
    );

    // Ricalcola il subtotale
    const subtotal = updatedItems.reduce((total, item) => total + (item.price * item.quantity), 0);
    const total = subtotal + (order.shippingCost || 0);

    await updateDoc(orderRef, {
      items: updatedItems,
      subtotal,
      total
    });

    return { id: orderId, ...order, items: updatedItems, subtotal, total };
  } catch (error) {
    console.error('Errore nell\'aggiornamento dell\'item:', error);
    throw error;
  }
};

export const updateOrderShipping = async (shopId, orderId, shippingCost) => {
  try {
    const orderRef = doc(db, 'shops', shopId, 'orders', orderId);
    const orderDoc = await getDoc(orderRef);
    
    if (!orderDoc.exists()) {
      throw new Error('Ordine non trovato');
    }

    const order = orderDoc.data();
    const total = order.subtotal + shippingCost;

    await updateDoc(orderRef, {
      shippingCost,
      total
    });

    return { id: orderId, ...order, shippingCost, total };
  } catch (error) {
    console.error('Errore nell\'aggiornamento della spedizione:', error);
    throw error;
  }
};

// Helper condiviso per costruire la query base con filtri
const buildProductsQuery = (shopId, filters = {}) => {
  const productsRef = collection(db, SHOPS_COLLECTION, shopId, PRODUCTS_SUBCOLLECTION);
  let q = query(productsRef);

  if (filters.category && filters.category !== 'all') {
    q = query(q, where('category', '==', filters.category));
  }

  if (filters.searchQuery) {
    // Ricerca case-insensitive: lowerBound usa prima lettera maiuscola (viene prima in Unicode)
    // upperBound usa tutto minuscolo + \uf8ff (viene dopo in Unicode)
    const term = filters.searchQuery;
    const lowerBound = term.charAt(0).toUpperCase() + term.slice(1).toLowerCase();
    const upperBound = term.toLowerCase() + '\uf8ff';
    q = query(q,
      where('name', '>=', lowerBound),
      where('name', '<=', upperBound)
    );
  }

  return q;
};

// Funzioni per la paginazione dei prodotti
export const getShopProductsPaginated = async (shopId, pageSize = 50, lastDoc = null, filters = {}) => {
  let baseQuery = buildProductsQuery(shopId, filters);

  // Firestore richiede che il primo orderBy sia sullo stesso campo del range filter
  // Con searchQuery usiamo solo orderBy('name') per evitare la necessità di un indice composito
  if (filters.searchQuery) {
    baseQuery = query(baseQuery, orderBy('name'));
  } else {
    baseQuery = query(baseQuery, orderBy('createdAt', 'desc'));
  }

  let q = query(baseQuery, limit(pageSize));

  if (lastDoc) {
    q = query(q, startAfter(lastDoc));
  }

  const snapshot = await getDocs(q);
  const products = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  const lastVisible = snapshot.docs[snapshot.docs.length - 1];

  return { products, lastVisible, hasMore: snapshot.docs.length === pageSize };
};

/**
 * Carica un batch di prodotti e restituisce i cursori per ogni "pagina" nel batch.
 * Molto più efficiente di getShopProductsPaginated quando si salta a pagine lontane:
 * invece di N query da pageSize documenti ciascuna, si fanno ~N/STRIDE query.
 *
 * @returns {{ products: Array, pageCursors: Array<DocumentSnapshot> }}
 *   pageCursors[i] è il lastVisible per la (i+1)-esima pagina dentro il batch
 */
export const getProductsBatchWithCursors = async (shopId, batchSize, pageSize, startAfterDoc = null, filters = {}) => {
  let baseQuery = buildProductsQuery(shopId, filters);

  if (filters.searchQuery) {
    baseQuery = query(baseQuery, orderBy('name'));
  } else {
    baseQuery = query(baseQuery, orderBy('createdAt', 'desc'));
  }

  let q = query(baseQuery, limit(batchSize));
  if (startAfterDoc) {
    q = query(q, startAfter(startAfterDoc));
  }

  const snapshot = await getDocs(q);
  const products = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

  // Estrai cursori per ogni pagina all'interno del batch
  const pageCursors = [];
  for (let i = pageSize - 1; i < snapshot.docs.length; i += pageSize) {
    pageCursors.push(snapshot.docs[i]);
  }

  return { products, pageCursors, hasMore: snapshot.docs.length === batchSize };
};

export const getProductsCount = async (shopId, filters = {}) => {
  const snapshot = await getCountFromServer(buildProductsQuery(shopId, filters));
  return snapshot.data().count;
}; 